import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Work Orders — LIVE.
 *
 * The whole resource: `POST`, `GET` (collection and detail), `PUT`, `DELETE`.
 *
 * ── What the collection does and does not serve ───────────────────────────
 * `list_work_orders` answers `{work_orders, page, page_size, total,
 * total_pages, counts}`. `counts.status` and `counts.source` are taken BEFORE
 * the status/priority/source filters apply, which is exactly what a kanban
 * needs: the column headers describe the whole scoped board, not the slice a
 * chip left behind.
 *
 * It does NOT serve the pickers. There is no assignee list and no asset list on
 * this payload, so the form reads the directory and the hierarchy directly —
 * see `WorkOrdersPage`. That is the right shape anyway: both are other
 * resources, and folding them into every page of a work-order list would send
 * the whole estate down the wire fifty rows at a time.
 *
 * ── Two permissions ───────────────────────────────────────────────────────
 * The list, the detail read and the update accept `work_orders.manage` OR
 * `work_orders.update_assigned`. Create and delete require `work_orders.manage`
 * alone. So a technician who may progress the jobs assigned to them cannot
 * raise or destroy one, and the screen has to offer those two controls
 * accordingly.
 *
 * ── This is the seam ──────────────────────────────────────────────────────
 * The enums below are exported from here and imported by the fixture, never the
 * other way round — a page that reached into `mocks/` would bundle the demo
 * layer into production. `features/operations/api.js` also reads this endpoint
 * for the public page's summary; the two coexist and share a cache entry.
 */

/**
 * The board's columns, in order — `WorkOrderStatus`, all five of it.
 *
 * ── Two columns went, one arrived ─────────────────────────────────────────
 * This was six, with `pendingApproval` and `approved` between draft and in
 * progress, carried over from the design prototype. Neither is a member of the
 * server's enum, so the board was drawing an approval gate nothing enforces —
 * and an order parked in Approved would have been refused by the first real
 * write. `cancelled` is the mirror image: a status the API has had all along
 * and the board had no column for, which left Delete as the only way to retire
 * an order somebody had decided against. Deleting destroys the record;
 * cancelling keeps it, with its reason, where the next person will find it.
 *
 * Restoring the approval step is backend work — two enum members and a
 * transition check. Recorded in BACKEND-GAPS.md §8.
 *
 * `onHold` sits between `inProgress` and `completed` because that is where work
 * lands when it stalls, but nothing ADVANCES into it — see `nextStateOf`.
 */
export const WO_STATES = ['draft', 'inProgress', 'onHold', 'completed', 'cancelled']

/**
 * App spelling ↔ the wire's. `WorkOrderStatus` is snake_case and this app is
 * not, and one mapping in one place beats `in_progress` appearing in JSX.
 */
const STATUS_TO_WIRE = {
  draft: 'draft',
  inProgress: 'in_progress',
  onHold: 'on_hold',
  completed: 'completed',
  cancelled: 'cancelled',
}
const STATUS_FROM_WIRE = Object.fromEntries(
  Object.entries(STATUS_TO_WIRE).map(([app, wire]) => [wire, app]),
)

/**
 * One wire status, in this app's spelling.
 *
 * Exported for the public page, which reads the raw collection through
 * `features/operations` rather than through `toOrder` — it wants one drafted
 * order and none of the rest of the mapping. Without this it would interpolate
 * `in_progress` into a translation key that does not exist.
 */
export const statusFromWire = (status) => STATUS_FROM_WIRE[status] ?? status

/**
 * `WorkOrderPriority`. Severity-first for display; the values are the enum's.
 *
 * `critical`, not `urgent`. The column has always said critical and so does the
 * design prototype; this app alone had renamed it, which made every filter and
 * every badge a translation of a value nothing else used.
 */
export const WO_PRIORITIES = ['critical', 'high', 'medium', 'low']

/**
 * `WorkOrderSource`. `manual`, not `human` — same correction as `critical`.
 *
 * Not a field a person may set — see `createWorkOrder`.
 */
export const WO_SOURCES = ['ai', 'manual']

/** Longest `notes` the column accepts — `Field(max_length=2000)`. */
export const NOTES_MAX_LENGTH = 2000

/** The two views. Both are the same collection; only the layout differs. */
export const WO_VIEWS = ['board', 'table']

/**
 * Where an order goes next, and it is THIS CLIENT'S opinion.
 *
 * `WorkOrderUpdate.status` is a bare enum with no transition check, so the API
 * accepts draft → completed without complaint. This used to arrive on the
 * record as `nextState`, computed server-side, and the comment beside it said
 * the server was the authority — it never was; the fixture was.
 *
 * So the map lives here, named for what it is, and the Move button offers one
 * step rather than a free choice. Nothing but this file enforces it. The gap is
 * in BACKEND-GAPS.md §8, and closing it means a state machine server-side.
 */
const WO_NEXT = {
  draft: 'inProgress',
  inProgress: 'completed',
  onHold: 'inProgress',
  completed: null,
  cancelled: null,
}

export const nextStateOf = (order) => WO_NEXT[order.status] ?? null

/** Whether an order still needs somebody. Terminal states are the two ends. */
export const isOpen = (order) => order.status !== 'completed' && order.status !== 'cancelled'

/** Pausing is reachable only from work that is actually running. */
export const canHold = (order) => order.status === 'inProgress'

/** Cancelling is reachable from anywhere still open, and from nowhere else. */
export const canCancel = (order) => isOpen(order)

/**
 * Wire → app.
 *
 * ── Three things are derived here rather than served ──────────────────────
 * `overdue`, `open` and the next state all used to be fields on the payload.
 * None of them is a column: they are functions of `due_date` and `status`, and
 * a server that sent them would be sending a second copy of two facts it had
 * already sent. Derived once, here, so the board and the detail panel cannot
 * disagree about whether the same order is late.
 *
 * `demo` carries what the backend has no column for — the parts list, the
 * safety gates, the CMMS write-back, the estimate. It is optional and absent
 * against the real endpoint, and every consumer treats it as absent-by-default
 * rather than assuming the fixture. See BACKEND-GAPS.md §8.
 *
 * `title` is either an i18n key (the orders Forge AI drafted, which are product
 * copy and identical on every demo) or free text somebody typed. The renderer
 * resolves it with `defaultValue`, so neither is wrong. `titleText` is the raw
 * string, which is what search matches on.
 */
/**
 * Exported, because ANOMALIES embeds work orders now.
 *
 * `AnomalyOut.work_orders` is a list of `WorkOrderListItem` — the same shape this
 * collection serves — so the anomaly detail screen needs this mapping and must
 * not grow a second copy of it. A work order is this module's record wherever it
 * appears, and two mappers would be two chances to disagree about what `overdue`
 * means.
 */
export const orderFromWire = (row) => {
  const status = STATUS_FROM_WIRE[row.status] ?? row.status
  const dueDate = row.due_date ?? null
  const demo = row.demo ?? {}

  const order = {
    id: row.id,
    /* The number a person reads and quotes. Distinct from `id`, which is the
       row key — the fixture happens to use one string for both and the real API
       does not, so nothing renders `id`. */
    serialNumber: row.serial_number ?? row.id,
    title: row.title,
    titleText: row.titleText ?? row.title,
    notes: row.notes ?? '',
    status,
    priority: row.priority,
    source: row.source,

    /* A LIST, because the column is. The board and the form still work one
       assignee at a time; carrying the list means widening them later is a UI
       change and not another contract change. */
    assigneeIds: row.assignee_ids ?? [],
    assignees: demo.assignees ?? [],

    /* A calendar date, `YYYY-MM-DD`. Not an instant: `due_date` is a `date`
       column and the day is the whole of the fact, so formatting it against a
       timezone would move it across midnight for half the estate. */
    dueDate,

    plantId: row.plant_id ?? null,
    lineId: row.line_id ?? null,
    machineId: row.machine_id ?? null,
    /* `{plant, line, machine}` — names only. `hierarchy_names` serves no code,
       so nothing may print one; the machine's own record is where that lives. */
    hierarchy: row.hierarchy ?? {},

    anomalyId: row.anomaly_id ?? null,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,

    demo: {
      estimateHours: demo.estimateHours ?? null,
      parts: demo.parts ?? [],
      gates: demo.gates ?? [],
      blockedByParts: demo.blockedByParts ?? [],
      writeBack: demo.writeBack ?? null,
      anomaly: demo.anomaly ?? null,
    },
  }

  order.open = isOpen(order)
  order.overdue = Boolean(dueDate) && order.open && dueDate < todayISO()
  return order
}

/** The private name the rest of this file already uses. */
const toOrder = orderFromWire

/** Today as `YYYY-MM-DD`, in the reader's own zone — the day a due date means. */
export function todayISO() {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

/** Whole days from today to a `YYYY-MM-DD`. Negative is overdue. */
export function daysUntil(dueDate) {
  if (!dueDate) return null
  const ms = Date.parse(`${dueDate}T00:00:00`) - Date.parse(`${todayISO()}T00:00:00`)
  return Math.round(ms / 86_400_000)
}

/**
 * The largest page the server will serve — `Query(..., le=200)`.
 *
 * It is also what the BOARD asks for. A kanban showing cards 26 to 50 of a
 * backlog is not a kanban, and the fixture used to answer an unpaged request
 * for exactly that reason — but the real endpoint has no such mode, so the
 * board takes the largest page there is and the column counts come from
 * `counts.status`, which is over the whole board rather than the page. A
 * backlog past 200 open orders therefore draws 200 cards under honest totals,
 * which is the truthful failure rather than a silent one.
 */
export const MAX_PAGE_SIZE = 200

/**
 * The collection.
 *
 * Params are snake_case because this is a real FastAPI signature and a
 * camelCase key is silently ignored — the server would answer the unfiltered
 * page 1 and the screen would show the wrong rows under the right heading.
 *
 * @param {{
 *   status?: string, priority?: string, assigneeId?: string, source?: string,
 *   q?: string, page?: number, pageSize?: number,
 * }} params
 */
export async function fetchWorkOrders(params = {}, signal) {
  const { status, priority, assigneeId, source, q, page = 1, pageSize = 50 } = params

  const data = await get(endpoints.workOrders.list, {
    signal,
    params: {
      // `all` is this screen's sentinel for an unset filter; the wire wants the
      // key absent, not the word.
      ...(status && status !== 'all' ? { status: STATUS_TO_WIRE[status] ?? status } : {}),
      ...(priority && priority !== 'all' ? { priority } : {}),
      ...(source && source !== 'all' ? { source } : {}),
      ...(assigneeId && assigneeId !== 'all' ? { assignee_id: assigneeId } : {}),
      ...(q?.trim() ? { search: q.trim() } : {}),
      sort_by: 'due_date',
      sort_dir: 'asc',
      page,
      page_size: pageSize,
    },
  })

  const statusCounts = data?.counts?.status ?? {}
  const sourceCounts = data?.counts?.source ?? {}

  return {
    items: (data?.work_orders ?? []).map(toOrder),
    total: data?.total ?? 0,
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,

    /* The board's column headers, re-keyed into this app's spelling. Counted
       server-side over the whole scoped board BEFORE the status/priority/source
       filters, so a chip narrows the cards without rewriting the totals above
       them — which is the opposite of what the fixture used to do and is the
       better answer: a column header reports what is in the column, and
       "In progress 4" above one visible card is how a reader learns a filter is
       on. */
    columns: Object.fromEntries(
      WO_STATES.map((state) => [state, statusCounts[STATUS_TO_WIRE[state]] ?? 0]),
    ),

    /* Both derived from the same counts rather than asked for separately.
       `open` is everything that is not finished one way or the other — the two
       terminal statuses are `completed` and `cancelled`, and an open count that
       forgot the second would carry abandoned work forever. */
    stats: {
      open:
        Object.entries(statusCounts)
          .filter(([key]) => key !== 'completed' && key !== 'cancelled')
          .reduce((sum, [, count]) => sum + count, 0),
      ai: sourceCounts.ai ?? 0,
    },
  }
}

/** One order. */
export async function fetchWorkOrder(id, signal) {
  return toOrder(await get(endpoints.workOrders.detail(id), { signal }))
}

/**
 * Raise one.
 *
 * `source` is deliberately NOT in the payload. The prototype's form has Source
 * as a select with AI in it, so a person can mark their own order AI-generated
 * — but provenance is recorded, not chosen, and the AI badge on this board
 * means Forge AI drafted the order. `WorkOrderSource` is set once at creation,
 * server-side, and the model's own comment says so.
 *
 * Neither is `status`: `_WorkOrderFields` marks it Update-only and every order
 * starts as a draft. The prototype's form lets you create one directly as
 * Completed.
 *
 * The parent is exactly one of plant/line/machine — `ExactlyOneParentMixin`
 * refuses zero or two, and it refuses them as a Pydantic `model_validator`,
 * which returns FastAPI's 422 shape rather than the field-keyed envelope. So
 * the form guarantees a parent before sending. The asset picker returns a
 * machine, which is the level a work order is nearly always raised at.
 */
export async function createWorkOrder({
  title,
  notes,
  priority,
  assigneeIds,
  dueDate,
  machineId,
  lineId,
  plantId,
  anomalyId,
}) {
  return toOrder(
    await post(endpoints.workOrders.create, {
      title,
      /* Nullable, and therefore still required to be PRESENT — `notes` is
         `str | None` with no default, and omitting a nullable key is a 422 in
         this API. It has cost four creates historically. */
      notes: notes?.trim() || null,
      priority,
      assignee_ids: assigneeIds ?? [],
      // A calendar date, `YYYY-MM-DD`, which is what the column is.
      due_date: dueDate,
      // Exactly one of the three, and all three keys present. The picker returns
      // a machine, which is the level a work order is nearly always raised at.
      plant_id: plantId ?? null,
      line_id: lineId ?? null,
      machine_id: machineId ?? null,
      anomaly_id: anomalyId || null,
    }),
  )
}

/**
 * Save an order.
 *
 * ── PUT, and a FULL replace ───────────────────────────────────────────────
 * `WorkOrderUpdate` is `title`, `priority`, `assignee_ids`, `due_date`, `notes`
 * AND `status`, every one of them required. This was a PATCH that sent only the
 * changed key, on the reasoning that advancing a state and editing a title are
 * different acts — which is true, and is not the contract. Against the real
 * endpoint a one-key body is a 422 naming the five it left out.
 *
 * So both callers send the whole record and differ only in which field they
 * changed. That is also why `useWorkOrder`'s cache must not go stale: a replace
 * built from a stale copy silently restores old values, the same trap
 * `updateSensor` documents.
 */
export async function saveWorkOrder({ id, title, notes, priority, status, assigneeIds, dueDate }) {
  return toOrder(
    await put(endpoints.workOrders.detail(id), {
      title,
      notes: notes?.trim() || null,
      priority,
      status: STATUS_TO_WIRE[status] ?? status,
      assignee_ids: assigneeIds ?? [],
      due_date: dueDate,
    }),
  )
}

/**
 * Move an order along the workflow.
 *
 * A full replace like every other write, carrying the order's own current
 * values for the five fields it is not changing — see `saveWorkOrder` for why
 * there is no narrower call to make.
 *
 * ── Those values are READ FIRST, not taken from the caller ───────────────
 * `notes` is on `WorkOrderOut` and not on the list item, and the board's Move
 * button holds a list row. Carrying that row's fields back sent `notes: null`
 * and wiped the order's notes on every move. So the current detail is fetched
 * and only `status` is changed — which also means a title or due date somebody
 * else changed since the board loaded is not silently put back.
 */
export async function advanceWorkOrder({ order, status }) {
  const current = await fetchWorkOrder(order.id)
  return saveWorkOrder({
    id: current.id,
    title: current.titleText,
    notes: current.notes,
    priority: current.priority,
    status,
    assigneeIds: current.assigneeIds,
    dueDate: current.dueDate,
  })
}

/** Remove one. Destroys the record — see `cancelled` for the other option. */
export async function deleteWorkOrder(id) {
  await del(endpoints.workOrders.detail(id))
  return { id, deleted: true }
}

/**
 * The assignees' names, from `assignee_ids` and a `useUserNames` map.
 *
 * The wire carries ids only, so `assignees` is empty on every live row and is
 * kept as the fixture's fallback. An id the directory has not named (still
 * loading, or past its one page) still counts — it comes back as `null`, so a
 * caller can tell "someone, unnamed" from nobody.
 */
export function assigneeNames(order, names) {
  if (order.assigneeIds.length) return order.assigneeIds.map((id) => names?.get(id) ?? null)
  return order.assignees.map((person) => person.name)
}

/** Names joined; an unnamed id reads "Assigned", none reads "Unassigned". */
export function assigneeText(names, t) {
  if (!names.length) return t('app:workOrders.unassigned')
  return names.map((name) => name ?? t('app:workOrders.assignedUnnamed')).join(', ')
}
